import { useEffect, useState } from "react";
import { Plus, Trash2, Sparkles, Pencil } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PRIORITY_LABELS } from "@/lib/taskHelpers";

export default function TaskTemplates() {
  const { currentWorkspace } = useWorkspace();
  const [templates, setTemplates] = useState([]);
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);
  const [applying, setApplying] = useState(null);

  const load = async () => {
    if (!currentWorkspace) return;
    const { data } = await api.get(`/task-templates`, { params: { workspace_id: currentWorkspace.workspace_id } });
    setTemplates(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [currentWorkspace]);

  const apply = async (tpl) => {
    setApplying(tpl.template_id);
    try {
      const { data } = await api.post(`/task-templates/${tpl.template_id}/apply`, {});
      toast.success(`Added ${data.created} tasks from template`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not apply template");
    } finally {
      setApplying(null);
    }
  };
  const del = async (tpl) => {
    if (!window.confirm(`Delete "${tpl.name}" template?`)) return;
    await api.delete(`/task-templates/${tpl.template_id}`);
    toast.success("Template deleted");
    load();
  };

  return (
    <div className="flex flex-col gap-6" data-testid="task-templates-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="bloq-eyebrow">Task templates</p>
          <h1 className="bloq-headline text-4xl sm:text-5xl mt-2">Repeatable task sets, one click away.</h1>
          <p className="text-sm text-[#4a4a52] mt-2">Apply a template to populate your task list with a tested checklist.</p>
        </div>
        <button onClick={() => { setEditing(null); setShow(true); }} className="bloq-btn-primary inline-flex items-center gap-2" data-testid="new-template-button">
          <Plus className="w-4 h-4" /> New template
        </button>
      </header>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {templates.map((t) => (
          <div key={t.template_id} className="bloq-card p-5 flex flex-col gap-3" data-testid={`template-card-${t.template_id}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="bloq-headline text-lg text-[#005e7a]">{t.name}</p>
                {t.is_default && <span className="bloq-pill mt-1 inline-block">Default</span>}
              </div>
              <Sparkles className="w-4 h-4 text-[#005e7a]" />
            </div>
            {t.description && <p className="text-xs text-[#4a4a52]">{t.description}</p>}
            <ul className="text-xs text-[#4a4a52] flex flex-col gap-1">
              {(t.tasks || []).slice(0, 5).map((tk, i) => (
                <li key={i} className="flex items-center gap-2">
                  <span className="w-1 h-1 rounded-full bg-[#82828c]" />
                  {tk.title} {tk.priority && <span className="text-[10px] uppercase tracking-widest text-[#82828c]">· {tk.priority}</span>}
                </li>
              ))}
              {(t.tasks || []).length > 5 && (
                <li className="text-[10px] text-[#82828c] uppercase tracking-widest">+ {t.tasks.length - 5} more</li>
              )}
            </ul>
            <div className="flex gap-2 mt-auto">
              <button onClick={() => apply(t)} disabled={applying === t.template_id} className="bloq-btn-primary text-xs px-3 py-1.5 flex-1" data-testid={`apply-template-${t.template_id}`}>
                {applying === t.template_id ? "Applying…" : "Apply"}
              </button>
              <button onClick={() => { setEditing(t); setShow(true); }} className="bloq-btn-secondary text-xs px-3 py-1.5" data-testid={`edit-template-${t.template_id}`}>
                <Pencil className="w-3 h-3" />
              </button>
              {!t.is_default && (
                <button onClick={() => del(t)} className="bloq-btn-secondary text-xs px-3 py-1.5 text-[#dc2626]" data-testid={`delete-template-${t.template_id}`}>
                  <Trash2 className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
      <TemplateDialog
        open={show}
        onOpenChange={(o) => { setShow(o); if (!o) setEditing(null); }}
        template={editing}
        onSaved={() => { load(); setShow(false); setEditing(null); }}
      />
    </div>
  );
}

function TemplateDialog({ open, onOpenChange, template, onSaved }) {
  const { currentWorkspace } = useWorkspace();
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [tasks, setTasks] = useState([{ title: "", priority: "medium" }]);

  useEffect(() => {
    if (template) {
      setName(template.name);
      setDesc(template.description || "");
      setTasks(template.tasks?.length ? template.tasks : [{ title: "", priority: "medium" }]);
    } else {
      setName("");
      setDesc("");
      setTasks([{ title: "", priority: "medium" }]);
    }
  }, [template, open]);

  const submit = async () => {
    const valid = tasks.filter((t) => t.title.trim());
    if (!name.trim() || valid.length === 0) {
      toast.error("Template needs a name and at least one task");
      return;
    }
    try {
      if (template) {
        await api.patch(`/task-templates/${template.template_id}`, { name, description: desc, tasks: valid });
      } else {
        await api.post(`/task-templates`, { workspace_id: currentWorkspace.workspace_id, name, description: desc, tasks: valid });
      }
      toast.success(template ? "Template updated" : "Template created");
      onSaved();
    } catch { toast.error("Could not save"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader><DialogTitle>{template ? "Edit template" : "New template"}</DialogTitle></DialogHeader>
        <div className="flex flex-col gap-3 mt-2">
          <input className="bloq-input" placeholder="Template name" value={name} onChange={(e) => setName(e.target.value)} data-testid="template-name-input" />
          <textarea rows={2} className="bloq-input" placeholder="Description (optional)" value={desc} onChange={(e) => setDesc(e.target.value)} data-testid="template-desc-input" />
          <div className="flex flex-col gap-2">
            <p className="text-xs uppercase tracking-widest text-[#82828c] font-medium">Tasks</p>
            {tasks.map((t, i) => (
              <div key={i} className="flex gap-2" data-testid={`template-task-${i}`}>
                <input
                  className="bloq-input flex-1"
                  placeholder={`Task #${i + 1}`}
                  value={t.title}
                  onChange={(e) => { const next = [...tasks]; next[i] = { ...next[i], title: e.target.value }; setTasks(next); }}
                />
                <Select value={t.priority || "medium"} onValueChange={(v) => { const next = [...tasks]; next[i] = { ...next[i], priority: v }; setTasks(next); }}>
                  <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(PRIORITY_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
                <button onClick={() => setTasks(tasks.filter((_, idx) => idx !== i))} className="bloq-btn-secondary px-2 text-[#dc2626]" data-testid={`template-remove-${i}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
            <button onClick={() => setTasks([...tasks, { title: "", priority: "medium" }])} className="bloq-btn-secondary self-start inline-flex items-center gap-2 text-xs" data-testid="template-add-task">
              <Plus className="w-3 h-3" /> Add task
            </button>
          </div>
          <button onClick={submit} className="bloq-btn-primary mt-2" data-testid="template-save">
            {template ? "Save changes" : "Create template"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
